import {
  type BedOption,
  type BedType,
  type CalendarDate,
  compareDates,
  type PlanDto,
  type SojournDetailDto,
} from '@room-mates/shared';

export type ItineraryEventKind = 'arrival' | 'departure' | 'move';

export interface ItineraryEvent {
  readonly kind: ItineraryEventKind;
  readonly guestId: string;
  readonly description: string;
}

export interface ItineraryDay {
  readonly date: CalendarDate;
  readonly events: readonly ItineraryEvent[];
  readonly note: string;
}

/** A spreadsheet reads a leading BOM as "this file is UTF-8", not literal text. */
const UTF8_BOM = '\uFEFF';

/** The itinerary as CSV text, Excel- and Numbers-compatible. */
export function itineraryToCsv(
  days: readonly ItineraryDay[],
  headers: readonly [string, string, string],
): string {
  const rows = days.map((day) => [
    day.date,
    day.events.map((event) => event.description).join('; '),
    day.note,
  ]);
  return UTF8_BOM + [headers, ...rows].map((row) => row.map(csvField).join(',')).join('\r\n');
}

function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * One line per date something happens: a guest arrives or departs, or —once
 * a plan exists — moves to a different bed. Dates with nothing going on are
 * left out entirely, since the point is to summarise the trip's logistics,
 * not to enumerate every day of it.
 *
 * The plan is optional because a sojourn's arrivals and departures are known
 * from the moment guests are added, well before anyone has generated a plan.
 */
export interface ItineraryStrings {
  arrives(name: string): string;
  departs(name: string): string;
  movesTo(name: string, from: string, to: string): string;
  bedLabel(type: BedType, option: BedOption): string;
}

export function buildItinerary(
  sojourn: SojournDetailDto,
  plan: PlanDto | null,
  strings: ItineraryStrings,
): ItineraryDay[] {
  const eventsByDate = new Map<CalendarDate, ItineraryEvent[]>();
  const addEvent = (date: CalendarDate, event: ItineraryEvent): void => {
    const list = eventsByDate.get(date) ?? [];
    list.push(event);
    eventsByDate.set(date, list);
  };

  for (const guest of sojourn.guests) {
    for (const stay of guest.stays) {
      addEvent(stay.start, {
        kind: 'arrival',
        guestId: guest.guestId,
        description: strings.arrives(guest.guestName),
      });
      addEvent(stay.end, {
        kind: 'departure',
        guestId: guest.guestId,
        description: strings.departs(guest.guestName),
      });
    }
  }

  if (plan !== null) {
    for (const move of planMoves(plan, strings)) {
      addEvent(move.date, {
        kind: 'move',
        guestId: move.guestId,
        description: move.description,
      });
    }
  }

  const notesByDate = new Map(sojourn.dayNotes.map((entry) => [entry.date, entry.note]));

  return [...eventsByDate.keys()].sort(compareDates).map((date) => ({
    date,
    events: eventsByDate.get(date) ?? [],
    note: notesByDate.get(date) ?? '',
  }));
}

interface Move {
  readonly date: CalendarDate;
  readonly guestId: string;
  readonly description: string;
}

/** Every point where a guest's bed changes from one segment they occupy to the next. */
function planMoves(plan: PlanDto, strings: ItineraryStrings): Move[] {
  const byGuest = new Map<string, (typeof plan.assignments)[number][]>();
  for (const assignment of plan.assignments) {
    const list = byGuest.get(assignment.guestId) ?? [];
    list.push(assignment);
    byGuest.set(assignment.guestId, list);
  }

  const moves: Move[] = [];
  for (const assignments of byGuest.values()) {
    const sorted = [...assignments].sort((a, b) => a.segmentIndex - b.segmentIndex);
    for (let index = 1; index < sorted.length; index += 1) {
      const previous = sorted[index - 1];
      const current = sorted[index];
      if (previous === undefined || current === undefined || previous.bedId === current.bedId) {
        continue;
      }
      const segment = plan.segments[current.segmentIndex];
      if (segment === undefined) {
        continue;
      }
      moves.push({
        date: segment.start,
        guestId: current.guestId,
        description: strings.movesTo(
          current.guestName,
          strings.bedLabel(previous.bedType, previous.bedOption),
          strings.bedLabel(current.bedType, current.bedOption),
        ),
      });
    }
  }
  return moves;
}
